import React, { useEffect, useState, useMemo } from 'react';
import api from '../../services/api';
//import { useLocation } from 'react-router-dom';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { DateBox } from "devextreme-react/date-box";
import DataGrid, {
  Column,
  Paging,
  Pager,
} from 'devextreme-react/data-grid';
import { LoadPanel } from 'devextreme-react/load-panel';

const schema = yup.object().shape({
  airline: yup.string().required("Required"),
  departDate: yup.date().required("Required"),
  departCity: yup.string().required("Required"),
  arrivalDate: yup.date().required("Required"),
  arrivalCity: yup.string().required("Required"),
});

const FlightForm = ({ id, onChange, onSubmit, selectedIds, isEditMode = false }) => {
  const [flight, setFlight] = useState({});
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const {
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
  
        // Fetch flight details if editing
        if (id) {
          const flightResponse = await api.get(`/flights/${id}`);
          const flightData = flightResponse.data;
          
          setFlight(flightData);
          reset({
            airline: flightData.airline,
            flightCode: flightData.flightCode,
            departDate: flightData.departDate ? new Date(flightData.departDate) : null,
            arrivalDate: flightData.arrivalDate ? new Date(flightData.arrivalDate) : null,
            departCity: flightData.departCity,
            arrivalCity: flightData.arrivalCity,
            departTerminal: flightData.departTerminal,
            arrivalTerminal: flightData.arrivalTerminal
          });
        }
  
        // Fetch applicant details
        if (selectedIds?.length > 0) {
          const applicantsResponse = await api.post('/Applicants/selected', selectedIds, {
            transformResponse: [
              // Handle both JSON and comma-separated responses
              (data) => {
                if (!data.trim()) return [];
                try {
                  return JSON.parse(data);
                } catch {
                  return data.split(',').map(id => id.trim()).filter(Boolean);
                }
              }
            ]
          });
          setApplicants(applicantsResponse.data);
        }
      } catch (error) {
        console.error("Data fetch error:", {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        
        setError(
          error.response?.data?.message || 
          error.message || 
          'Failed to load data'
        );
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [id, selectedIds, reset]);

  const columns = useMemo(
    () => [
      { field: 'fullName', caption: 'Full Name' },
      { field: 'passportNo', caption: 'Passport No' },
      { field: 'pasportIssuedDate', caption: 'Date of Issue' },
      { field: 'pasportExpiryDate', caption: 'Date of Expiry' },        
    ],
    []
  );

  return (
    <div className="container mt-4" style={{ maxWidth: "875px" }}>
      <h5 className="es-label-title">{isEditMode ? "EDIT FLIGHT DETAIL" : "ADD FLIGHT DETAIL"}</h5>
      <div className="card p-3">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-1 col-form-label es-field-label">Airline</label>
                <div className="col-sm-5">
                  <select {...register("airline")} className="form-control select-with-arrow">
                    <option value="">Select Airline</option>
                    <option value="FLY NAS">FLY NAS</option>
                    <option value="Ethiopian Airlines (ET)">Ethiopian Airlines (ET)</option>
                    <option value="Emirates (EK)">Emirates (EK)</option>
                    <option value="Qatar Airways (Q.C.S.C) (Q.R)">Qatar Airways (Q.C.S.C) (Q.R)</option>
                    <option value="Fly Dubai (FZ)">Fly Dubai (FZ)</option>
                    <option value="Saudi Arabian Airlines(SV)">Saudi Arabian Airlines(SV)</option>
                    <option value="Extend Return">Extend Return</option>
                  </select>
                  {errors.airline && <span className="text-danger">Required</span>}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-1 col-form-label es-field-label">Flight Code</label>
                <div className="col-sm-5">
                  <input {...register("flightCode")} className="form-control" />
                </div>
              </div>
            </div>
          </div>
          
          <div className="row mb-0">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-1 col-form-label es-field-label">Depart Date</label>
                <div className="col-sm-5 d-flex align-items-center">
                  <DateBox
                    value={watch("departDate")}
                    displayFormat="dd/MM/yyyy"
                    placeholder="DD/MM/YYYY"
                    onValueChanged={(e) => setValue("departDate", e.value)}
                    min={new Date("2000-01-01")}
                  />
                  {errors.departDate && <span className="text-danger">Required</span>}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-1 col-form-label es-field-label">Arrival Date</label>
                <div className="col-sm-5 d-flex align-items-center">
                  <DateBox
                    value={watch("arrivalDate")}
                    displayFormat="dd/MM/yyyy"
                    placeholder="DD/MM/YYYY"
                    onValueChanged={(e) => setValue("arrivalDate", e.value)}
                    min={new Date("2000-01-01")}
                  />
                  {errors.arrivalDate && <span className="text-danger">Required</span>}
                </div>
              </div>
            </div>
          </div>
          
          <div className="row mb-0">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-1 col-form-label es-field-label">Depart City</label>
                <div className="col-sm-5">
                  <select {...register("departCity")} className="form-control select-with-arrow">
                    <option value="">Select City</option>
                    <option value="Abha">Abha</option>
                    <option value="Al Ahsa">Al Ahsa</option>
                    <option value="Al Jouf">Al Jouf</option>
                    <option value="Dammam">Dammam</option>
                    <option value="Gassim">Gassim</option>
                    <option value="Hail">Hail</option>
                    <option value="Jeddah">Jeddah</option>
                    <option value="Madina">Madina</option>
                    <option value="Najran">Najran</option>
                    <option value="Neom">Neom</option>
                    <option value="Riyadh">Riyadh</option>
                    <option value="Taif">Taif</option>
                    <option value="Ammam">Ammam</option>
                    <option value="Doha">Doha</option>
                    <option value="Dubai">Dubai</option>
                    <option value="Addis Ababa">Addis Ababa</option>                      
                  </select>
                  {errors.departCity && <span className="text-danger">Required</span>}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-1 col-form-label es-field-label">Arrival City</label>
                <div className="col-sm-5">
                  <select {...register("arrivalCity")} className="form-control select-with-arrow">
                    <option value="">Select City</option>
                    <option value="Abha">Abha</option>
                    <option value="Al Ahsa">Al Ahsa</option>
                    <option value="Al Jouf">Al Jouf</option>
                    <option value="Dammam">Dammam</option>
                    <option value="Gassim">Gassim</option>
                    <option value="Hail">Hail</option>
                    <option value="Jeddah">Jeddah</option>
                    <option value="Madina">Madina</option>
                    <option value="Najran">Najran</option>
                    <option value="Neom">Neom</option>
                    <option value="Riyadh">Riyadh</option>
                    <option value="Taif">Taif</option>
                    <option value="Ammam">Ammam</option>
                    <option value="Doha">Doha</option>
                    <option value="Dubai">Dubai</option>
                    <option value="Addis Ababa">Addis Ababa</option>                      
                  </select>
                  {errors.arrivalCity && <span className="text-danger">Required</span>}
                </div>
              </div>
            </div>
          </div>
          
          <div className="row mb-0">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-1 col-form-label es-field-label">Depart Terminal</label>
                <div className="col-sm-5">
                  <input {...register("departTerminal")} className="form-control" />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-1 col-form-label es-field-label">Arrival Terminal</label>
                <div className="col-sm-5">
                  <input {...register("arrivalTerminal")} className="form-control" />
                </div>
              </div>
            </div>
          </div>
          
          <h5 className="text-lg font-semibold mt-4">Selected Applicants</h5>
          <div className="row mb-0">
            <div className="col-md-12">
              {loading ? (
                <LoadPanel visible={true} />
              ) : (
                <DataGrid
                  dataSource={applicants}
                  showBorders
                  rowAlternationEnabled
                  allowColumnResizing
                  columnAutoWidth
                  height="240px"
                  width="815px"
                >
                  <Paging defaultPageSize={7} />
                  <Pager showPageSizeSelector allowedPageSizes={[7, 10, 20]} showInfo />
                  {columns.map(({ field, caption }) => (
                    <Column 
                      key={field} 
                      dataField={field} 
                      caption={caption} 
                      allowFiltering 
                      customizeText={
                        field === 'pasportIssuedDate' || field === 'pasportExpiryDate'
                          ? (cellInfo) => {
                              if (!cellInfo.value) return '';
                              const date = new Date(cellInfo.value);
                              return isNaN(date.getTime()) ? cellInfo.value : date.toLocaleDateString('en-GB');
                            }
                          : undefined
                      }
                    />
                  ))}
                </DataGrid>
              )}
            </div>
          </div>
          <div className="d-flex justify-content-end">
            <button type="submit" className="btn es-button">
              SAVE
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FlightForm;